import { useEffect, useRef, useState } from 'react';
import { useLang } from '@/hooks/useLang';

function useCountUp(target: number, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

export default function CatchSection() {
  const { t } = useLang();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const years = useCountUp(100, 2000, visible);
  const products = useCountUp(6, 1200, visible);
  const cases = useCountUp(50, 1800, visible);

  return (
    <section className="relative bg-[#0a0a0a] overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,0.3) 40px, rgba(255,255,255,0.3) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,0.3) 40px, rgba(255,255,255,0.3) 41px)'
      }}></div>

      {/* Top diagonal cut */}
      <div className="absolute top-0 left-0 right-0 h-16 bg-white" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 0, 0 100%)' }}></div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-8 pt-24 pb-20">
        {/* Label */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-px bg-[#c0392b]"></div>
          <span className="text-[#c0392b] text-xs font-bold uppercase tracking-[0.25em]">
            {t('Our Mission', 'ミッション')}
          </span>
        </div>

        {/* Main statement */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-8">
              {t(
                <>Not just processing<br /><span className="text-[#c0392b]">waste</span> —<br />creating <span className="text-[#c0392b]">value.</span></>,
                <>廃棄物を「処理」するのではなく、<br /><span className="text-[#c0392b]">良質なリサイクル原料</span>を「つくる」</>
              )}
            </h2>
            <p className="text-white/50 text-sm leading-loose mb-8">
              {t(
                'The EPS recycling landscape in North America is rapidly evolving. With growing pressure to reduce foam waste in landfills, municipalities and recyclers alike are seeking reliable, high-throughput solutions. We don\'t just process waste — we help you create valuable recycled material.',
                'プラスチックリサイクルを取り巻く環境は変化しています。廃棄物を「処理」するのではなく、良質なリサイクル原料を「つくる」。私たち山本製作所は、あるべき姿を思い描き、挑戦する人たちに選ばれるモノづくりを目指しています。'
              )}
            </p>
            <a
              href={`https://www.yamamoto-ss.co.jp/sp_recycle/concept/`}
              className="group inline-flex items-center gap-3 text-white text-sm font-bold border border-white/20 px-6 py-3 hover:border-[#c0392b] hover:text-[#c0392b] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {t('Read Our Philosophy', '開発への思いを読む')}
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>

          {/* Stats */}
          <div ref={sectionRef} className="grid grid-cols-3 gap-0">
            {[
              { value: years, suffix: '+', label: t('Years of Manufacturing', '製造の歴史'), sub: t('Since 1918', '1918年創業') },
              { value: products, suffix: '', label: t('Product Lines', '製品ライン'), sub: t('EPS, PET & more', 'EPS・PET他') },
              { value: cases, suffix: '+', label: t('Case Studies', '導入事例'), sub: t('Across industries', '各業種') },
            ].map((stat, i) => (
              <div key={i} className={`flex flex-col items-center justify-center py-10 px-4 ${i < 2 ? 'border-r border-white/10' : ''}`}>
                <div className="text-5xl md:text-6xl font-black text-white tabular-nums leading-none mb-1">
                  {stat.value}{stat.suffix}
                </div>
                <div className="text-[#c0392b] text-xs font-bold uppercase tracking-widest mt-2 text-center">{stat.label}</div>
                <div className="text-white/30 text-[10px] mt-1 text-center">{stat.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom diagonal cut */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-white" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }}></div>
    </section>
  );
}
